import { useEffect, useState } from 'react';
import { Link, createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTAS, type EstadoMaquina, type Papel, type PapelMaquina } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import { socketDoPainel } from '../socket/painel';
import {BotaoLeve, Cartao,  Selo} from '../componentes/layout';
import { CampoDeEscolha } from '../componentes/campos';
import { usarConfirmacao } from '../componentes/Modal';
import { Tela } from '../workspace/Tela';
import { mensagemDoErro } from '../erros';
import { tempoRelativo } from '../formato';

interface MaquinaNaLista {
  id: string;
  nome: string;
  papel: PapelMaquina;
  estado: EstadoMaquina;
  versaoAgente: string | null;
  versaoClaude: string | null;
  /** A versão do Codex daquela máquina, ou `null` quando ela não o tem. */
  versaoCodex: string | null;
  so: string | null;
  capacidadeSdk: number;
  visibilidade: Papel;
  memoriaLivreMb: number | null;
  /** Quantas o daemon tem vivas agora; é esta conta que barra a criação. */
  sessoesNoDaemon?: number | null;
  ultimoPulsoEm: string | null;
  sessoesAtivas: number;
}

interface CredencialCriada {
  id: string;
  prefixo: string;
  segredo: string;
  nome: string;
}


function Maquinas(): React.JSX.Element {
  const cliente = useQueryClient();
  const papel = usarSessao((e) => e.usuario?.papel);
  const ehAdmin = papel === 'ADMIN';

  const [nome, setNome] = useState('');
  const [papelMaquina, setPapelMaquina] = useState<PapelMaquina>('PC');
  const [erro, setErro] = useState('');
  const [credencial, setCredencial] = useState<CredencialCriada | null>(null);
  const [copiada, setCopiada] = useState(false);

  const { data: maquinas = [], isLoading } = useQuery({
    queryKey: ['maquinas'],
    queryFn: () => chamar<MaquinaNaLista[]>(ROTAS.maquinas),
  });

  // Presença ao vivo: o agente que conecta ou cai muda o selo sem recarregar.
  useEffect(() => {
    const socket = socketDoPainel();
    const aoMudar = (): void => {
      void cliente.invalidateQueries({ queryKey: ['maquinas'] });
    };
    socket.on('maquina.estado', aoMudar);
    socket.on('sessao.estado', aoMudar);
    return () => {
      socket.off('maquina.estado', aoMudar);
      socket.off('sessao.estado', aoMudar);
    };
  }, [cliente]);

  const criar = useMutation({
    mutationFn: () =>
      chamar<MaquinaNaLista>(ROTAS.maquinas, {
        method: 'POST',
        corpo: { nome: nome.trim().toLowerCase(), papel: papelMaquina },
      }),
    onSuccess: () => {
      setNome('');
      setErro('');
      void cliente.invalidateQueries({ queryKey: ['maquinas'] });
    },
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Use apenas minúsculas, dígitos e hífen no nome.' })),
  });

  const gerarCredencial = useMutation({
    mutationFn: (id: string) =>
      chamar<CredencialCriada>(`${ROTAS.maquinas}/${id}/tokens`, {
        method: 'POST',
        corpo: { nome: 'daemon' },
      }),
    onSuccess: (dados) => {
      setCredencial(dados);
      setCopiada(false);
      setErro('');
    },
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Use apenas minúsculas, dígitos e hífen no nome.' })),
  });

  const remover = useMutation({
    mutationFn: (id: string) => chamar<void>(`${ROTAS.maquinas}/${id}`, { method: 'DELETE' }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['maquinas'] }),
    onError: (e) => setErro(mensagemDoErro(e, 'Não foi possível concluir.', { validacao: 'Use apenas minúsculas, dígitos e hífen no nome.' })),
  });

  const [confirmar, dialogo] = usarConfirmacao();

  return (
    <Tela>
      {dialogo}
      {erro ? (
        <p className="rounded-md border border-red-900 bg-red-950/40 px-4 py-2 text-sm text-red-200">
          {erro}
        </p>
      ) : null}

      {credencial ? (
        <Cartao
          titulo="Credencial do agente"
          acao={<BotaoLeve onClick={() => setCredencial(null)}>Fechar</BotaoLeve>}
        >
          <p className="text-sm text-zinc-400">
            Copie agora: o segredo aparece uma única vez. No banco fica só o hash, e nem o painel
            consegue mostrá-lo de novo. Cole em <code className="text-zinc-300">TOKEN_DISPOSITIVO</code>{' '}
            no arquivo de ambiente do agente.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs text-emerald-300">
              {credencial.segredo}
            </code>
            <BotaoLeve
              onClick={() => {
                void navigator.clipboard.writeText(credencial.segredo).then(() => setCopiada(true));
              }}
            >
              {copiada ? 'copiado' : 'copiar'}
            </BotaoLeve>
          </div>
        </Cartao>
      ) : null}

      {ehAdmin ? (
        <Cartao titulo="Nova máquina">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              criar.mutate();
            }}
          >
            <label className="flex-1 space-y-1.5">
              <span className="text-sm text-zinc-400">Nome</span>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="desktop-birunce"
                className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none transition focus:border-zinc-500"
              />
            </label>
            <CampoDeEscolha<PapelMaquina>
              rotulo="Papel"
              className="w-52"
              valor={papelMaquina}
              opcoes={[
                { id: 'PC', rotulo: 'computador', descricao: 'máquina de trabalho' },
                { id: 'VPS', rotulo: 'servidor', descricao: 'a VPS, com os guard-rails' },
              ]}
              aoEscolher={setPapelMaquina}
            />
            <button
              type="submit"
              disabled={nome.trim().length < 2 || criar.isPending}
              className="rounded-md bg-zinc-100 px-4 py-2 font-medium text-zinc-900 transition hover:bg-white disabled:opacity-50"
            >
              Cadastrar
            </button>
          </form>
        </Cartao>
      ) : null}

      <Cartao titulo="Máquinas">
        {isLoading ? (
          <p className="text-sm text-zinc-500">Carregando…</p>
        ) : maquinas.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Nenhuma máquina ainda. Cadastre uma, gere a credencial e ligue o agente nela.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {maquinas.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-100">{m.nome}</span>
                    <Selo ligada={m.estado === 'ONLINE'} />
                    <span className="text-xs text-zinc-500">
                      {m.papel === 'VPS' ? 'servidor' : 'computador'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {m.sessoesAtivas} de {m.capacidadeSdk} sessão(ões) · agente{' '}
                    {m.versaoAgente ?? '—'} · Claude {m.versaoClaude ?? '—'}
                    {/*
                      O Codex só aparece em quem o tem: um "Codex —" em toda
                      máquina da frota seria ruído, e ruído se aprende a
                      ignorar junto com o que importava.
                    */}
                    {m.versaoCodex ? ` · Codex ${m.versaoCodex}` : ''} · último sinal{' '}
                    {tempoRelativo(m.ultimoPulsoEm)}
                    {m.memoriaLivreMb !== null ? ` · ${m.memoriaLivreMb} MB livres` : ''}
                  </p>
                  {/*
                    O número do daemon, e só quando ele DISCORDA do painel.

                    Quem recusa a criação de sessão é o teto do daemon, contando
                    o que ele tem na memória; o painel contava linhas no banco.
                    Quando os dois divergem, a pessoa levava um "no limite" com
                    vagas livres na tela e nenhum caminho para entender. Mostrar
                    sempre seria ruído; mostrar na divergência é o diagnóstico.
                  */}
                  {m.sessoesNoDaemon !== null &&
                  m.sessoesNoDaemon !== undefined &&
                  m.sessoesNoDaemon !== m.sessoesAtivas ? (
                    <p className="mt-0.5 text-xs text-amber-400/90">
                      o daemon tem {m.sessoesNoDaemon} viva(s) — é esta conta que decide a vaga
                    </p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Link to="/maquinas/$id/arquivos" params={{ id: m.id }}>
                    <BotaoLeve disabled={m.estado !== 'ONLINE'}>Arquivos</BotaoLeve>
                  </Link>
                </div>
                {ehAdmin ? (
                  <div className="flex gap-2">
                    <BotaoLeve
                      disabled={gerarCredencial.isPending}
                      onClick={() => gerarCredencial.mutate(m.id)}
                    >
                      Gerar credencial
                    </BotaoLeve>
                    <BotaoLeve
                      disabled={remover.isPending}
                      onClick={() =>
                        confirmar({
                          titulo: 'Remover máquina',
                          texto: `A máquina ${m.nome} sai do painel e o daemon dela perde a credencial. As sessões já registradas continuam no histórico.`,
                          rotuloConfirmar: 'Remover',
                          perigosa: true,
                          aoConfirmar: () => remover.mutate(m.id),
                        })
                      }
                    >
                      Remover
                    </BotaoLeve>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </Tela>
  );
}

export const rotaMaquinas = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/maquinas',
  component: Maquinas,
});
